import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { GUARDS } from '@app/constants/guards';
import { StorageService, UserService } from '../services.module';
// Guards
import { AdminAuthGuard } from './admin-auth/admin-auth.guard';
import { AdvisoryAuthGuard } from './advisory-auth/advisory-auth.guard';
import { DirectionAuthGuard } from './direction-auth/direction-auth.guard';
import { EmployeeAuthGuard } from './employee-auth/employee-auth.guard';
import { ManagerAuthGuard } from './manager-auth/manager-auth.guard';
import { OperationsAuthGuard } from './operations-auth/operations-auth.guard';

@Injectable({
  providedIn: 'root'
})
export class MasterGuard implements CanActivate {

  constructor(
    private storage: StorageService,
    private router: Router,
    private _userService: UserService
  ) {}

  private route: ActivatedRouteSnapshot;
  private state: RouterStateSnapshot;
  private acumCanActivate = false;

  /**
   * Function that gets triggered when the route is hit
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    this.route = route;
    this.state = state;

    if (!route.data) {
      return Promise.resolve(true);
    }

    // This.route.data.guards is an array of strings set in routing configuration
    if (!this.route.data.guards || !this.route.data.guards.length) {
      return Promise.resolve(true);
    }

    // Execute all the guards and check the accumulator
    return this.executeGuards().then(() => {
      if (this.acumCanActivate) {
        this.acumCanActivate = false;
        return Promise.resolve(true);
      } else {
        this.acumCanActivate = false;
        this.router.navigate(['home']);
        return Promise.resolve(false);
      }
    });
  }

  /**
   * Execute the guards sent in the route data
   * It goes through all the guards of the data
   */
  private executeGuards(guardIndex: number = 0): Promise<boolean> {
    return this.activateGuard(this.route.data.guards[guardIndex])
      .then((data) => {
        // We accumulate the result of the guard
        this.acumCanActivate = this.acumCanActivate || data;
        if (guardIndex < this.route.data.guards.length - 1) {
          return this.executeGuards(guardIndex + 1);
        } else {
          return data; // finally
        }
      })
      .catch(() => {
        return Promise.reject(false);
      });
  }

  /**
   * Create an instance of the guard and fire canActivate method returning a promise
   */
  private activateGuard(guardKey: string): Promise<boolean> {
    let guard: AdminAuthGuard | DirectionAuthGuard | OperationsAuthGuard
      | EmployeeAuthGuard | ManagerAuthGuard | AdvisoryAuthGuard;

    switch (guardKey) {
      case GUARDS.AdminGuard:
        guard = new AdminAuthGuard(this.storage, this.router, this._userService);
        break;
      case GUARDS.DirectionGuard:
        guard = new DirectionAuthGuard(this.storage, this.router, this._userService);
        break;
      case GUARDS.ManagerGuard:
        guard = new ManagerAuthGuard(this.storage, this.router, this._userService);
        break;
      case GUARDS.OperationsGuard:
        guard = new OperationsAuthGuard(this.storage, this.router, this._userService);
        break;
      case GUARDS.AdvisoryGuard:
        guard = new AdvisoryAuthGuard(this.storage, this.router, this._userService);
        break;
      case GUARDS.EmployeeGuard:
        guard = new EmployeeAuthGuard(this.storage, this.router, this._userService);
        break;
      default:
        break;
    }

    return guard.canActivate();
  }

}
